import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, ShoppingBag } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { CardContent, CardIcon } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ListCard } from '@/components/ListCard'
import { ListCardHeader } from '@/components/ListCardHeader'
import { ListPagination } from '@/components/ListPagination'
import { RetailLayoutField } from '@/components/RetailLayoutField'
import { RetailProductFilters } from '@/components/RetailProductFilters'
import { RetailProductTable } from '@/components/RetailProductTable'
import { SearchInput } from '@/components/SearchInput'
import { useRetailCreativeCreation } from '@/hooks/useRetailCreativeCreation'
import { useRetailProductList } from '@/hooks/useRetailProductList'

interface RetailProductPickerDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  lineItemId: string
}

/** Picks retail products and creates a creative for each on the line item. */
export function RetailProductPickerDialog({
  open,
  onOpenChange,
  lineItemId,
}: RetailProductPickerDialogProps) {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const list = useRetailProductList(search, open)
  const creation = useRetailCreativeCreation(lineItemId, open)
  const count = list.selection.length

  const create = async () => {
    const result = await creation.create(lineItemId, list.selection, list.merchants)
    if (result.failures.length > 0) return
    list.clearSelection()
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !creation.creating && onOpenChange(next)}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-card sm:max-w-[90vw]">
        <DialogHeader>
          <DialogTitle>{t('retailProducts.addTitle')}</DialogTitle>
          <DialogDescription>{t('retailProducts.addDescription')}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <RetailLayoutField
            layouts={creation.layouts}
            loading={creation.layoutsLoading}
            value={creation.layoutId}
            onChange={creation.setLayoutId}
            disabled={creation.creating}
          />

          <ListCard tone="creative">
            <ListCardHeader
              title={
                <>
                  <CardIcon icon={ShoppingBag} />
                  {t('retailProducts.productCount', { count: list.totalCount })}
                </>
              }
              controls={
                <>
                  <SearchInput value={search} onChange={(v) => list.refilter(() => setSearch(v))} />
                  <RetailProductFilters
                    filters={list.filters}
                    onChange={list.setFilters}
                    facets={list.facets}
                    loading={list.facetsLoading}
                  />
                </>
              }
            />
            <CardContent className="p-0">
              <RetailProductTable
                products={list.products}
                loading={list.loading}
                merchants={list.merchants}
                selection={list.selection}
                onSelectionChange={list.changeSelection}
              />
            </CardContent>
            <ListPagination
              page={list.page}
              pageSize={list.pageSize}
              totalCount={list.totalCount}
              onPageChange={list.setPage}
              summary={t('retailProducts.productCount', { count: list.totalCount })}
            />
          </ListCard>
        </div>

        <DialogFooter className="items-center">
          {count > 0 && (
            <span className="mr-auto text-sm text-(--ad-text-muted)">
              {t('common.selected_count', { count })}
            </span>
          )}
          <Button
            type="button"
            variant="ghost"
            disabled={creation.creating}
            onClick={() => onOpenChange(false)}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            disabled={count === 0 || !creation.layoutId || creation.creating}
            onClick={create}
          >
            {creation.creating && <Loader2 className="size-4 animate-spin" />}
            {t('retailProducts.createCreatives', { count })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
